import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, Download, Home, Ticket, AlertTriangle } from "lucide-react";
import { EthiopianCross } from "@/components/EthiopianCross";
import { api, API } from "@/lib/api";

const MAX_ATTEMPTS = 8;

export default function Confirmation() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | pending | expired | error
  const [purchase, setPurchase] = useState(null);
  const [qr, setQr] = useState(null);

  const poll = useCallback(async (attempt = 0) => {
    if (!sessionId) { setState("error"); return; }
    if (attempt >= MAX_ATTEMPTS) { setState("pending"); return; }
    try {
      const { data } = await api.get(`/checkout/status/${sessionId}`);
      if (data.payment_status === "paid") {
        setPurchase(data.purchase);
        setState("paid");
        const first = data.purchase?.ticket_numbers?.[0];
        if (first) {
          api.get(`/tickets/qr/${first}`).then(({ data: q }) => setQr(q.qr)).catch(() => {});
        }
        return;
      }
      if (data.status === "expired") { setState("expired"); return; }
      setTimeout(() => poll(attempt + 1), 2000);
    } catch (e) {
      setTimeout(() => poll(attempt + 1), 2000);
    }
  }, [sessionId]);

  useEffect(() => { poll(0); }, [poll]);

  return (
    <div className="min-h-screen bg-[#FAF9F6] flex flex-col items-center justify-center px-6 py-16">
      <div className="w-full max-w-2xl">
        {state === "checking" && (
          <div className="text-center" data-testid="confirmation-checking">
            <Loader2 className="w-12 h-12 text-[#9E1B1B] animate-spin mx-auto" />
            <h1 className="font-heading text-3xl font-semibold mt-6">Confirming your payment…</h1>
            <p className="text-[#4A4A4A] mt-2">Please wait while we generate your ticket numbers.</p>
          </div>
        )}

        {state === "paid" && purchase && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="bg-white border border-[#E2D9C8] rounded-2xl shadow-[0_8px_40px_rgba(0,0,0,0.08)] overflow-hidden" data-testid="confirmation-success">
            <div className="bg-[#2D6A4F] text-white px-8 py-10 text-center">
              <CheckCircle2 className="w-16 h-16 mx-auto" />
              <h1 className="font-heading text-3xl sm:text-4xl font-bold mt-4">Payment Confirmed!</h1>
              <p className="mt-2 text-white/90">Thank you, {purchase.full_name}. You're entered in the drawing.</p>
            </div>
            <div className="p-8">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <p className="text-xs tracking-[0.2em] uppercase text-[#4A4A4A]">Your Ticket Number{purchase.ticket_numbers.length > 1 ? "s" : ""}</p>
                  <p className="text-sm text-[#4A4A4A] mt-1">{purchase.quantity} ticket(s) · ${purchase.amount.toFixed(2)} paid</p>
                </div>
                {qr && <img src={qr} alt="QR code" className="w-24 h-24 rounded-lg border border-[#E2D9C8]" data-testid="ticket-qr" />}
              </div>
              <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="ticket-numbers">
                {purchase.ticket_numbers.map((tn) => (
                  <div key={tn} className="ticket-number text-center text-xl font-bold text-[#9E1B1B] bg-[#F3EFE6] border border-[#E2D9C8] rounded-lg py-3">{tn}</div>
                ))}
              </div>

              <div className="mt-6 rounded-lg bg-[#F3EFE6] p-5 text-sm text-[#4A4A4A]">
                <p className="font-semibold text-[#1A1A1A] mb-1">Event Details</p>
                <p>Grand Drawing Date: <strong>January 24, 2027</strong></p>
                <p>St. Michael Ethiopian Orthodox Church · Columbus, Ohio</p>
                <p className="mt-1">A confirmation SMS has been sent to your phone (if enabled).</p>
              </div>

              <div className="mt-6 flex flex-wrap gap-3">
                <a href={`${API}/receipt/${purchase.id}`} target="_blank" rel="noreferrer"
                  className="flex items-center gap-2 bg-[#9E1B1B] hover:bg-[#7A1414] text-white px-6 py-3 rounded-md font-semibold transition-colors" data-testid="download-receipt">
                  <Download className="w-5 h-5" /> Download PDF Receipt
                </a>
                <Link to="/" className="flex items-center gap-2 border-2 border-[#9E1B1B] text-[#9E1B1B] hover:bg-[#9E1B1B] hover:text-white px-6 py-3 rounded-md font-semibold transition-colors" data-testid="back-home">
                  <Home className="w-5 h-5" /> Back to Home
                </Link>
              </div>
            </div>
          </motion.div>
        )}

        {(state === "pending") && (
          <div className="text-center" data-testid="confirmation-pending">
            <AlertTriangle className="w-12 h-12 text-[#F4A261] mx-auto" />
            <h1 className="font-heading text-3xl font-semibold mt-6">Still processing…</h1>
            <p className="text-[#4A4A4A] mt-2">Your payment is taking a moment. Refresh this page shortly, or check back soon.</p>
            <button onClick={() => { setState("checking"); poll(0); }} className="mt-6 bg-[#9E1B1B] text-white px-6 py-3 rounded-md font-semibold" data-testid="retry-btn">Check Again</button>
          </div>
        )}

        {(state === "expired" || state === "error") && (
          <div className="text-center" data-testid="confirmation-error">
            <EthiopianCross className="w-24 h-24 mx-auto opacity-70" />
            <h1 className="font-heading text-3xl font-semibold mt-6">Payment not completed</h1>
            <p className="text-[#4A4A4A] mt-2">Your checkout session expired or was canceled. No charge was made.</p>
            <Link to="/#buy" className="inline-flex items-center gap-2 mt-6 bg-[#9E1B1B] text-white px-6 py-3 rounded-md font-semibold"><Ticket className="w-5 h-5" /> Try Again</Link>
          </div>
        )}
      </div>
    </div>
  );
}
